import { Placement } from '@ng-bootstrap/ng-bootstrap';
import { ComponentType } from '@angular/cdk/portal';
import {
  Component,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepicker } from '@angular/material/datepicker';
import { AbstractValueAccessor, MakeProvider } from '../abstract-value-accessor';
import { CustomDateAdapter } from '../../date-picker-adapter';
import { DatePickerHeaderComponent } from '../date-picker-header/date-picker-header.component';
import { delay } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    MakeProvider(DatePickerComponent),
    {
      provide: MAT_DATE_FORMATS, useValue: {
        display: {
          dateInput: { year: 'numeric', month: 'short', day: 'numeric' },
          monthYearLabel: { year: 'numeric', month: 'long' },
          dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
          monthYearA11yLabel: { year: 'numeric', month: 'long' },
        },
      }
    },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  standalone: false,
})
export class DatePickerComponent extends AbstractValueAccessor implements OnInit {
  @ViewChild('picker')
  private picker: MatDatepicker<any>;

  required: boolean;
  index: number;
  validateOnDirty: boolean;
  datePickerHeader: ComponentType<any>;

  @HostBinding('class.dirty')
  get dirty(): boolean {
    return this.formControl?.dirty;
  }

  @HostBinding('class.invalid')
  get invalid(): boolean {
    return this.formControl?.invalid;
  }

  @HostBinding('class.has-value')
  get hasValue(): boolean {
    return this.value;
  }

  @HostBinding('class.disabled')
  get disabled(): boolean {
    return this.isDisabled;
  }

  @HostBinding('class.active')
  isActive: boolean;

  @Input()
  infoTooltip: string | TemplateRef<any>;

  @Input()
  infoTooltipPlacement: Placement;

  @Input()
  infoTooltipClass: string;

  @Input()
  label: string;

  @Input()
  titleWithColon = true;

  @Input()
  labelParamsObj: any;

  @Input()
  svgIconPath: string;

  @Input()
  type: string;

  @Input()
  placeholder: string;

  @Input()
  minDate: Date;

  @Input()
  maxDate: Date;

  @Input()
  startAt: Date;

  @Input()
  isCustomHeader = true;

  @Input()
  hasIcon = true;

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-native
  select = new EventEmitter<any>();

  @Output()
  datepickerClosed = new EventEmitter<void>();

  @Output()
  private readonly dateChanged = new EventEmitter<string>();

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    this.datePickerHeader = this.isCustomHeader ? DatePickerHeaderComponent : null;

    // Even though picker closes after click on date,
    // in Cypress tests due to fast click after opening picker, it does not close and makes tests using this flaky.
    // This trick mitigates this as it forces picker to close right after value changes
    this.dateChanged.pipe(delay(500), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.picker.close());
  }

  get panelClass(): string[] {
    const classes = ['app-datepicker'];
    if (!this.type || this.type === 'orange') {
      classes.push('orange');
    }
    return classes;
  }

  dateChange(event: MatDatepickerInputEvent<Date>): void {
    try {
      const timeZoneDifference = Math.abs(event.value.getTimezoneOffset()); //convert to positive value of timezone.
      const timezoneRemoveNumber = 60000;
      event.value.setTime(event.value.getTime() + timeZoneDifference * timezoneRemoveNumber);
      this.value = event.value.toISOString();
      this.dateChanged.emit(this.value);
    } catch {
      this.formControl.setErrors({ invalidDate: true });
    }
  }

  openDatepicker(): void {
    if (!this.isDisabled) {
      this.picker.open();
    }
  }

  onDatepickerClosed(): void {
    this.datepickerClosed.emit();
  }
}
